import { AvailableCountry } from '../types/countries.js';

/**
 * Date/number locale resolution, shared by the client and (later) the server.
 *
 * There is no locale setting: it is derived from the user's UI language and
 * the current library's country. Language alone can't tell NZ English from
 * US English, and the country alone can't tell a Spanish-speaking user from
 * an English-speaking one, so both are needed.
 *
 * Canadian libraries use US formats, in English and Spanish (decided
 * 2026-11-02, D31): 03/04/2026, Mar 4, 2026, 12-hour clock, Sunday weeks.
 * Australian and New Zealand libraries share one locale, `en-NZ` (decided
 * 2026-11-03, D42): 04/03/2026, 4 Mar 2026, 12-hour clock, Monday weeks.
 *
 * Angular and dayjs spell locale ids differently (`en-NZ` vs `en-nz`), so a
 * resolved locale carries both. The Angular id is the canonical one — it is
 * what gets cached and compared.
 */
export interface ResolvedLocale {
    angular: string;
    dayjs: string;
}

export const DEFAULT_LOCALE: ResolvedLocale = { angular: 'en-US', dayjs: 'en' };

/** Every locale the app ships data for, keyed by Angular id. */
export const SUPPORTED_LOCALES: Record<string, ResolvedLocale> = {
    'en-US': DEFAULT_LOCALE,
    'en-NZ': { angular: 'en-NZ', dayjs: 'en-nz' },
    'es-US': { angular: 'es-US', dayjs: 'es-us' },
    es: { angular: 'es', dayjs: 'es' },
    sv: { angular: 'sv', dayjs: 'sv' }
};

const ENGLISH_BY_COUNTRY = {
    USA: 'en-US',
    CAN: 'en-US',
    AUS: 'en-NZ',
    NZL: 'en-NZ'
} satisfies Record<AvailableCountry, string>;

/** Countries whose Spanish speakers get es-US (US date order, Sunday weeks, 12-hour clock). */
const US_ORDER_COUNTRIES: ReadonlySet<AvailableCountry> = new Set<AvailableCountry>(['USA', 'CAN']);

/**
 * Resolve the locale for a UI language (`en` / `es` / `sv`) and a library
 * country. A missing country is treated as USA, matching the default in
 * `LibraryService.getLibrarySettings()`; an unknown language as English.
 */
export function resolveLocale(language: string | null | undefined, country: AvailableCountry | null | undefined): ResolvedLocale {
    const resolvedCountry = country ?? 'USA';
    if (language === 'sv') return SUPPORTED_LOCALES.sv;
    if (language === 'es') return US_ORDER_COUNTRIES.has(resolvedCountry) ? SUPPORTED_LOCALES['es-US'] : SUPPORTED_LOCALES.es;
    return SUPPORTED_LOCALES[ENGLISH_BY_COUNTRY[resolvedCountry]] ?? DEFAULT_LOCALE;
}

/**
 * Look up a supported locale by Angular id; anything else falls back to the default. A locale cached
 * before it was dropped (`en-CA`, D31; `en-AU`, D42) falls back too, and `AppLocaleService` then reloads once.
 */
export function getSupportedLocale(angularId: string | null | undefined): ResolvedLocale {
    return (angularId && SUPPORTED_LOCALES[angularId]) || DEFAULT_LOCALE;
}
